import { Button, Typography } from "@povio/ui";
import Image from "next/image";
import { useTranslation } from "react-i18next";

import { CancelIcon } from "@/assets/icons/general/CancelIcon";
import { PlusSquaredIcon } from "@/assets/icons/general/PlusSquaredIcon";

interface InstallAppBannerProps {
  onInstall: () => void;
  onDismiss: () => void;
}

/**
 * PWA Install Banner Component
 * Displays an inline banner with dog logo, install message, and action buttons
 * For iOS: Shows instructions on how to manually add to home screen
 * For Android/Chrome: Triggers the native install prompt
 */
export const InstallAppBanner = ({ onInstall, onDismiss }: InstallAppBannerProps) => {
  const { t } = useTranslation();

  return (
    <div className="mb-4 flex w-full flex-row flex-wrap items-center justify-between gap-4 px-6 xl:hidden [@media(min-width:400px)]:flex-nowrap">
      {/* Dog Logo */}
      <div className="mx-auto flex h-27 w-27 shrink-0 items-center justify-center overflow-hidden rounded-4xl">
        <Image
          src="/apple-touch-icon.png"
          alt="Dog Logo"
          width={100}
          height={100}
          className="h-24 w-24 object-cover"
        />
      </div>
      <div className="flex dm:w-full flex-col items-center gap-4">
        {/* Text Content */}
        <Typography
          variant="prominent-1"
          size="body-4"
        >
          {t("shared.pwa.bannerText")}
        </Typography>
        <div className="flex w-full shrink-0 items-center justify-center gap-2">
          <Button
            variant="contained"
            color="secondary"
            size="s"
            width="fill"
            icon={PlusSquaredIcon}
            iconClassName="h-4 w-4"
            className="w-1/2"
            onPress={onInstall}
          >
            {t("shared.pwa.addButton")}
          </Button>

          <Button
            variant="ghost"
            color="secondary"
            size="s"
            width="fill"
            icon={CancelIcon}
            iconClassName="h-5 w-5"
            className="w-1/2"
            onPress={onDismiss}
          >
            {t("shared.pwa.dismissButton")}
          </Button>
        </div>
      </div>

      {/* Action Buttons */}
    </div>
  );
};
